import { useActiveFeatureFlags, useFeatureFlagsReady } from '@infrastructure/featureFlags';
import useAuthMemberships from '@contexts/useAuthMemberships/useAuthMemberships';
import LoadingPageOverlay from '@components/common/LoadingPageOverlay';
import { useStalledAfter } from '@hooks/useStalledAfter';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { canAny } from '@hooks/useCapability';
import useAuth, { AuthContextType } from '@contexts/useAuth/useAuth';
import { NAVIGATION_CONFIG, sectionGateCapabilities } from '@config/navigation.config';
import type { CapabilityKey } from '@bett3r-dev/teselly-capabilities';
import React, { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router';
import { Forbidden403, RouteErrorReporter, StalledLoadPage } from '../pages/ErrorPages';
import { Protected, Public } from '../hocs/withAuthenticated';
import Layout from '../pages/Layout';
import { HOME_REDIRECT_ORDER, resolveHomePath } from '@domains/navigation';

/**
 * Fallback rendered while a route's chunk is in flight ( TV1-2322 ).
 *
 * Deliberately NOT the fixed, full-viewport overlay used by
 * `ProtectedHomeRedirect` / `GatedRoute` below: those cover the navbar because
 * they are still deciding *where* the user goes, whereas this one runs after the
 * destination is settled and the page is merely downloading. Covering the navbar
 * on every navigation would hide the control the user just clicked. It occupies
 * the content area so a navigation shows a loader rather than a blank frame.
 */
function RouteFallback() {
  return (
    <LoadingPageOverlay>
      <div style={{ height: '60vh' }}></div>
    </LoadingPageOverlay>
  );
}

type PageModule = { default: React.ComponentType };

/**
 * Route-level code splitting ( TV1-2322 ).
 *
 * Every page below is reached through `React.lazy`, so a route's module graph is
 * fetched only when that route actually renders. Previously all 30 pages were
 * static imports, which meant loading *any* route pulled in *every* route:
 * ~1000 module requests and 10.9s to the first widget on /dashboard in dev, and
 * a single 4.64 MB `index-*.js` entry chunk in production.
 *
 * `lazyPage` returns a component that already carries its own `Suspense`
 * boundary. That buys two things:
 *
 * 1. Call sites are unchanged — `<Dashboard />` still just renders a page, so
 *    the route table and `COMPONENT_MAP` below stay free of per-entry
 *    `Suspense` noise.
 * 2. The boundary sits *inside* the guards. `FlaggedRoute` / `GatedRoute` /
 *    `ProtectedHomeRedirect` decide and return their `<Navigate>` synchronously
 *    without ever rendering the lazy child, so a redirect never waits on — or
 *    even requests — the chunk of the page it is redirecting away from. Do not
 *    hoist the `Suspense` above the guards; that ordering is load-bearing.
 *
 * Kept eager on purpose:
 * - `Layout` — the shell every private route renders inside. Splitting it would
 *   only add a second serial round-trip ahead of every page chunk.
 * - `ErrorPages` ( `errorElement` and the `/403` destination ) — already in the
 *   entry chunk via `ErrorBoundary`, so splitting it would save nothing.
 * - `withAuthenticated`, `LoadingPageOverlay`, `@domains/navigation` and
 *   `@config/navigation.config` — guard and fallback infrastructure that has to
 *   run *before* any page resolves. `ProtectedHomeRedirect` picks the landing
 *   route from `HOME_REDIRECT_ORDER` + `NAVIGATION_CONFIG` alone; it needs no
 *   page module to decide.
 */
const lazyPage = ( loader: () => Promise<PageModule> ) => {
  const Page = lazy( loader );
  return function LazyPage() {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Page />
      </Suspense>
    );
  };
};

const Notifications = lazyPage(() => import( '../pages/Notifications' ));
const Orders = lazyPage(() => import( '../pages/Orders' ));
const Reports = lazyPage(() => import( '../pages/Reports' ));
const Authentication = lazyPage(() => import( '../pages/Authentication' ));
const ForgotPassword = lazyPage(() => import( '../pages/Authentication/ForgotPassword' ));
const ResetPassword = lazyPage(() => import( '../pages/Authentication/ResetPassword' ));
const SocialLoginError = lazyPage(() => import( '../pages/Authentication/SocialLoginError' ));
const TwoFactorRequired = lazyPage(() => import( '../pages/Authentication/TwoFactorRequired' ));
const Billing = lazyPage(() => import( '../pages/Billing' ));
const Dashboard = lazyPage(() => import( '../pages/Dashboard' ));
const Interactions = lazyPage(() => import( '../pages/Interactions' ));
const Integrations = lazyPage(() => import( '../pages/Integrations' ));
const Inventory = lazyPage(() => import( '../pages/Inventory' ));
const NotFoundTitle = lazyPage(() => import( '../pages/NotFound' ));
const Publications = lazyPage(() => import( '../pages/Publications' ));
const Subscription = lazyPage(() => import( '../pages/Subscription' ));
const Warehouses = lazyPage(() => import( '../pages/Warehouses' ));
// TV1-2158 (S7) — the procurement suppliers master.
const Suppliers = lazyPage(() => import( '../pages/Suppliers' ));
const Accounting = lazyPage(() => import( '../pages/Accounting' ));
const Invoices = lazyPage(() => import( '../pages/Invoices' ));
const ChannelInfo = lazyPage(() => import( '@domains/integrations/channels/components/ChannelInfo/ChannelInfo' ));
const ERPInfo = lazyPage(() => import( '@domains/integrations/erps/components/ERPInfo/ERPInfo' ));
const InvoicingInfo = lazyPage(() => import( '@domains/integrations/invoicing/components/InvoicingInfo/InvoicingInfo' ));
const LogisticsInfo = lazyPage(() => import( '@domains/integrations/logistics/components/LogisticsInfo/LogisticsInfo' ));
const MessagingInfo = lazyPage(() => import( '@domains/integrations/messaging/components/MessagingInfo/MessagingInfo' ));
const AccountSettings = lazyPage(() => import( '../pages/AccountSettings' ));
const UserSettings = lazyPage(() => import( '../pages/UserSettings' ));
const DecisionMatrixDemo = lazyPage(() => import( '../pages/Demos/DecisionMatrix/DecisionMatrixDemo' ));
const MCDADemo = lazyPage(() => import( '../pages/Demos/MCDA' ));
const BatchProcesses = lazyPage(() => import( '../pages/BatchProcesses' ));
const BatchProcessDetailPage = lazyPage(() => import( '../pages/BatchProcessDetail' ));
const Automation = lazyPage(() => import( '../pages/Automation' ));
const AutomationRuleDetailPage = lazyPage(() => import( '../pages/AutomationRuleDetail' ));
const AutomationRequestDetailPage = lazyPage(() => import( '../pages/AutomationRequestDetail' ));
const OperationsPage = lazyPage(() => import( '../pages/Operations' ));
const OperationReviewPage = lazyPage(() => import( '../pages/OperationReview' ));

// TV1-1690 ticket A-3: dev-only spreadsheet benchmark harness.
// Routes are conditionally registered below ONLY when `import.meta.env.DEV` is
// true (or VITE_ENABLE_DEV_TOOLS is set to 'true'). The page modules are
// imported via `React.lazy(() => import(...))` so Vite tree-shakes them out
// of production bundles — the chunk is only emitted if the route registers it.
const isDevToolsEnabled = import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEV_TOOLS === 'true';
const DevSpreadsheetBench = lazyPage(() => import( '../pages/_DevSpreadsheetBench' ));
const DevSpreadsheetBenchScrollSpike = lazyPage(() => import( '../pages/_DevSpreadsheetBench/ScrollSpike' ));

/**
 * Component map - maps paths to their React components.
 * This is separate from the route registry since components
 * can't be easily serialized/exported.
 */
const COMPONENT_MAP: Record<string, React.ComponentType> = {
  '/dashboard': Dashboard,
  '/reports': Reports,
  '/invoices': Invoices,
  '/accounting': Accounting,
  '/accounting/:tabId': Accounting,
  '/publications': Publications,
  '/publications/:tabId': Publications,
  '/publications/list/view/:publicationId': Publications,
  '/publications/list/edit/:publicationId': Publications,
  '/inventory': Inventory,
  '/inventory/:tabId': Inventory,
  '/inventory/list/view/:itemId': Inventory,
  '/inventory/list/edit/:itemId': Inventory,
  '/integrations': Integrations,
  '/integrations/:tabId': Integrations,
  '/integrations/channels/:integrationId': ChannelInfo,
  '/integrations/erps/:erpId': ERPInfo,
  '/integrations/invoicing/:invoicingId': InvoicingInfo,
  '/integrations/logistics/:logisticsId': LogisticsInfo,
  '/integrations/messaging/:messagingId': MessagingInfo,
  '/billing': Billing,
  '/subscription': Subscription,
  '/subscription/:tabId': Subscription,
  '/orders': Orders,
  '/orders/:tabId': Orders,
  '/orders/list': Orders,
  '/orders/list/view/:orderId': Orders,
  '/orders/list/edit/:orderId': Orders,
  '/orders/create': Orders,
  '/accountSettings': AccountSettings,
  '/accountSettings/:tabId': AccountSettings,
  '/userSettings': UserSettings,
  '/userSettings/:tabId': UserSettings,
  '/warehouses': Warehouses,
  '/suppliers': Suppliers,
  '/interactions': Interactions,
  '/interactions/:tabId': Interactions,
  '/notifications': Notifications,
  '/notifications/:tabId': Notifications,
  '/operations': OperationsPage,
  '/operations/review/:runId': OperationReviewPage
};

// Path -> read-capability lookup, derived from the same nav config that gates
// the navbar tabs and route guards. Keeps the landing-page choice in lock-step
// with `capabilityKey` so a user is never redirected to a route they'd be
// bounced out of with a `/403`.
const HOME_PATH_CAPABILITY: Record<string, CapabilityKey[]> = NAVIGATION_CONFIG.reduce(
  ( acc, section ) => {
    const keys = sectionGateCapabilities( section );
    if ( keys.length ) acc[ section.path ] = keys;
    return acc;
  },
  {} as Record<string, CapabilityKey[]>
);

// Section id -> the ANY-OF key set its route guard must admit. Same derivation as
// the sidebar section filter in `UserNavbar`, so the two cannot drift. [TV1-2259]
const SECTION_GATE: Record<string, CapabilityKey[]> = NAVIGATION_CONFIG.reduce(
  ( acc, section ) => {
    const keys = sectionGateCapabilities( section );
    if ( keys.length ) acc[ section.id ] = keys;
    return acc;
  },
  {} as Record<string, CapabilityKey[]>
);

function ProtectedHomeRedirect() {
  const flagsReady = useFeatureFlagsReady();
  const membershipsLoading = useAuthMemberships()?.isLoading ?? false;
  const activeFlags = useActiveFeatureFlags();
  const { user, capabilitiesReady } = useAuth() as AuthContextType;

  // Wait for the account-scoped flag set to settle before choosing a landing
  // route. Right after login the provider still holds the pre-auth/public flag
  // set (no landing-page flags in it), and `refetchFlags()` only swaps in the
  // real set ~1s later over the subscription. Deciding now would send entitled
  // users to /not-found. `membershipsLoading` covers the window from login until
  // the membership snapshot fires `refetchFlags()`; `!flagsReady` covers the
  // window from that refetch until the account snapshot lands. The same flag set
  // gates the destination in `FlaggedRoute`, so once it's ready both agree.
  // `!capabilitiesReady` keeps us on the loader until the query-first capability
  // hydration settles — the landing-route choice below calls `can( user, … )`,
  // which would otherwise skip capability-gated landings ( and fall through to
  // /not-found ) against an un-hydrated `{}` scope map. [TV1-1879]
  /*
   * TV1-2369 — none of these three has an unhappy path. If any never settles the
   * full-screen loader below spins forever, nothing throws for    * to catch, and the user is left with no explanation and nothing to click.
   */
  const bootStalled = useStalledAfter( membershipsLoading || !flagsReady || !capabilitiesReady );
  if ( bootStalled ) return <StalledLoadPage />;

  if ( membershipsLoading || !flagsReady || !capabilitiesReady ) {
    // Fixed, full-viewport overlay so it covers the navbar too (this route renders
    // inside Layout's <Outlet />, below <UserNavbar />), matching the full-screen
    // loader shown during login. Navbar z-index is 1–2, so 2000 sits well above it.
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
        <LoadingPageOverlay>
          <div style={{ height: '100vh' }}></div>
        </LoadingPageOverlay>
      </div>
    );
  }

  // First route whose feature flag is active AND (if capability-gated) the user
  // actually holds the read capability — otherwise the GatedRoute would bounce
  // them straight to /403. `HOME_REDIRECT_ORDER` ranks the /subscription pay
  // surface last (TV1-2077), so a de-entitled / suspended account (flags collapsed
  // to the billing surface only) falls through to /subscription instead of
  // /not-found, while a fully entitled user still lands on their first primary
  // route.
  const targetPath = resolveHomePath(
    HOME_REDIRECT_ORDER,
    ( flagKey ) => activeFlags.includes( flagKey ),
    ( path ) => {
      const capabilities = HOME_PATH_CAPABILITY[ path ];
      return canAny( user, capabilities ?? []);
    }
  );
  if ( targetPath ) {
    return <Navigate to={targetPath} replace />;
  }
  return <Navigate to="/not-found" replace />;
}

function FlaggedRoute({ flagKey, children }: { flagKey: string; children: React.ReactElement; }) {
  const enabled = useBooleanFlagValue( flagKey, false );
  if ( !enabled ) {
    return <Navigate to="/" replace />;
  }
  return children;
}

/**
 * Read-side capability gate ( TV1-1867 ). Sibling of `FlaggedRoute`: wraps a
 * route element and, when the authenticated user lacks the capability,
 * redirects to the dedicated 403 page instead of rendering the route
 * ( previously such users saw an empty list ).
 *
 * `capability` accepts one key or an ANY-OF list. Section routes pass
 * `SECTION_GATE[<sectionId>]` — the section key plus every tab key, derived from
 * `NAVIGATION_CONFIG` by `sectionGateCapabilities` — so this gate and the
 * sidebar section filter admit exactly the same set and a tab-only grant is not
 * inert. The per-tab filter in `useTabNavigation` still decides which tabs
 * render. [TV1-2259]
 *
 * Gating uses typed `CapabilityKey`s via the isomorphic `canAny` helper — a
 * renamed/removed capability is a compile error. No instance is passed: this is
 * nav/route-level gating, not per-instance.
 */
function GatedRoute({ capability, children }: { capability: CapabilityKey | CapabilityKey[]; children: React.ReactElement; }) {
  const { user, capabilitiesReady } = useAuth() as AuthContextType;
  const allowed = canAny( user, Array.isArray( capability ) ? capability : [capability]);
  // TV1-1879 — wait for the query-first capability hydration to settle before
  // deciding. Without this, a first render against an un-hydrated `{}` scope map
  // redirects to /403 — and since the guard is no longer mounted once we
  // navigate away, the later capability arrival can't re-evaluate, leaving the
  // user stranded on /403 (only a hard refresh recovered). Same full-viewport
  // overlay as ProtectedHomeRedirect so the navbar is covered too.
  if ( user && !capabilitiesReady ) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
        <LoadingPageOverlay>
          <div style={{ height: '100vh' }}></div>
        </LoadingPageOverlay>
      </div>
    );
  }
  if ( !allowed ) {
    return <Navigate to="/403" replace />;
  }
  return children;
}

const router = createBrowserRouter([
  {
    element: <Public />,
    errorElement: <RouteErrorReporter />,
    children: [
      // without layout (public)
      {
        path: 'auth',
        element: ( <Authentication /> )
      },
      {
        path: 'auth/forgot-password',
        element: ( <ForgotPassword /> )
      },
      {
        path: 'auth/reset-password',
        element: ( <ResetPassword /> )
      },
      {
        path: 'auth/error',
        element: ( <SocialLoginError /> )
      },
      {
        path: 'auth/two-factor',
        element: ( <TwoFactorRequired /> )
      },
      {
        path: '/',
        element: ( <Navigate to="/auth" replace /> )
      }
    ]
  },
  // private routes
  {
    element: <Protected fallbackElement={<Navigate to="/auth" replace={true} />} />,
    errorElement: <RouteErrorReporter />,
    children: [
      {
        element: <Layout />,
        children: [
          {
            index: true,
            element: <ProtectedHomeRedirect />
          },
          {
            path: 'dashboard',
            element: (
              <FlaggedRoute flagKey="dashboard">
                <Dashboard />
              </FlaggedRoute>
            )
          },
          {
            path: 'reports',
            element: (
              <FlaggedRoute flagKey="reports">
                <GatedRoute capability={SECTION_GATE.reports}>
                  <Reports />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'invoices',
            element: (
              <FlaggedRoute flagKey="invoices">
                <GatedRoute capability={SECTION_GATE.invoices}>
                  <Invoices />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'accounting',
            element: (
              <FlaggedRoute flagKey="accounting">
                <GatedRoute capability={SECTION_GATE.accounting}>
                  <Accounting />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'accounting/:tabId',
            element: (
              <FlaggedRoute flagKey="accounting">
                <GatedRoute capability={SECTION_GATE.accounting}>
                  <Accounting />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'publications',
            element: (
              <FlaggedRoute flagKey="publications">
                <GatedRoute capability={SECTION_GATE.publications}>
                  <Publications />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'publications/:tabId',
            element: (
              <FlaggedRoute flagKey="publications">
                <GatedRoute capability={SECTION_GATE.publications}>
                  <Publications />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'publications/list/view/:publicationId',
            element: (
              <FlaggedRoute flagKey="publications">
                <GatedRoute capability={SECTION_GATE.publications}>
                  <Publications />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'publications/list/edit/:publicationId',
            element: (
              <FlaggedRoute flagKey="publications">
                <GatedRoute capability={SECTION_GATE.publications}>
                  <Publications />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'inventory',
            element: (
              <FlaggedRoute flagKey="inventory">
                <GatedRoute capability={SECTION_GATE.inventory}>
                  <Inventory />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'inventory/:tabId',
            element: (
              <FlaggedRoute flagKey="inventory">
                <GatedRoute capability={SECTION_GATE.inventory}>
                  <Inventory />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'inventory/list/view/:itemId',
            element: (
              <FlaggedRoute flagKey="inventory">
                <GatedRoute capability={SECTION_GATE.inventory}>
                  <Inventory />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'inventory/list/edit/:itemId',
            element: (
              <FlaggedRoute flagKey="inventory">
                <GatedRoute capability={SECTION_GATE.inventory}>
                  <Inventory />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'integrations',
            element: (
              <FlaggedRoute flagKey="integrations">
                <GatedRoute capability={SECTION_GATE.integrations}>
                  <Integrations />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'integrations/:tabId',
            element: (
              <FlaggedRoute flagKey="integrations">
                <GatedRoute capability={SECTION_GATE.integrations}>
                  <Integrations />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'integrations/channels/:integrationId',
            element: (
              <FlaggedRoute flagKey="integrations">
                <GatedRoute capability={SECTION_GATE.integrations}>
                  <ChannelInfo />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'integrations/erps/:erpId',
            element: (
              <FlaggedRoute flagKey="integrations">
                <GatedRoute capability={SECTION_GATE.integrations}>
                  <ERPInfo />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'integrations/invoicing/:invoicingId',
            element: (
              <FlaggedRoute flagKey="integrations">
                <GatedRoute capability={SECTION_GATE.integrations}>
                  <InvoicingInfo />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'integrations/logistics/:logisticsId',
            element: (
              <FlaggedRoute flagKey="integrations">
                <GatedRoute capability={SECTION_GATE.integrations}>
                  <LogisticsInfo />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'integrations/messaging/:messagingId',
            element: (
              <FlaggedRoute flagKey="integrations">
                <GatedRoute capability={SECTION_GATE.integrations}>
                  <MessagingInfo />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'billing',
            element: (
              <FlaggedRoute flagKey="billing">
                <GatedRoute capability={SECTION_GATE.subscription}>
                  <Billing />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'subscription',
            element: (
              <FlaggedRoute flagKey="subscription">
                <GatedRoute capability={SECTION_GATE.subscription}>
                  <Subscription />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'subscription/:tabId',
            element: (
              <FlaggedRoute flagKey="subscription">
                <GatedRoute capability={SECTION_GATE.subscription}>
                  <Subscription />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'orders',
            element: (
              <FlaggedRoute flagKey="orders">
                <GatedRoute capability={SECTION_GATE.orders}>
                  <Orders />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'orders/:tabId',
            element: (
              <FlaggedRoute flagKey="orders">
                <GatedRoute capability={SECTION_GATE.orders}>
                  <Orders />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'orders/list',
            element: (
              <FlaggedRoute flagKey="orders">
                <GatedRoute capability={SECTION_GATE.orders}>
                  <Orders />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'orders/list/view/:orderId',
            element: (
              <FlaggedRoute flagKey="orders">
                <GatedRoute capability={SECTION_GATE.orders}>
                  <Orders />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'orders/list/edit/:orderId',
            element: (
              <FlaggedRoute flagKey="orders">
                <GatedRoute capability={SECTION_GATE.orders}>
                  <Orders />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'orders/create',
            element: (
              <FlaggedRoute flagKey="orders">
                <GatedRoute capability={SECTION_GATE.orders}>
                  <Orders />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'accountSettings',
            element: (
              <FlaggedRoute flagKey="account">
                <AccountSettings />
              </FlaggedRoute>
            )
          },
          {
            path: 'accountSettings/:tabId',
            element: (
              <FlaggedRoute flagKey="account">
                <AccountSettings />
              </FlaggedRoute>
            )
          },
          {
            path: 'userSettings',
            element: (
              <FlaggedRoute flagKey="account">
                <UserSettings />
              </FlaggedRoute>
            )
          },
          {
            path: 'userSettings/:tabId',
            element: (
              <FlaggedRoute flagKey="account">
                <UserSettings />
              </FlaggedRoute>
            )
          },
          {
            path: 'warehouses',
            element: (
              <FlaggedRoute flagKey="warehouses">
                <GatedRoute capability={SECTION_GATE.warehouses}>
                  <Warehouses />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            // TV1-2158 (S7). The gate resolves to `procurement:suppliers:read`
            // via `SECTION_GATE`, derived from NAVIGATION_CONFIG — so the route
            // guard and the sidebar admit exactly the same set. An OPERATOR
            // (read only) reaches the list; the manage-only affordances inside
            // are hidden by their own `useCan` gates.
            path: 'suppliers',
            element: (
              <FlaggedRoute flagKey="suppliers">
                <GatedRoute capability={SECTION_GATE.suppliers}>
                  <Suppliers />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: '403',
            element: ( <Forbidden403 /> )
          },
          {
            path: 'interactions',
            element: (
              <FlaggedRoute flagKey="interactions">
                <GatedRoute capability={SECTION_GATE.interactions}>
                  <Interactions />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'interactions/:tabId',
            element: (
              <FlaggedRoute flagKey="interactions">
                <GatedRoute capability={SECTION_GATE.interactions}>
                  <Interactions />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'notifications',
            element: (
              <FlaggedRoute flagKey="notifications">
                <Notifications />
              </FlaggedRoute>
            )
          },
          {
            path: 'notifications/:tabId',
            element: (
              <FlaggedRoute flagKey="notifications">
                <Notifications />
              </FlaggedRoute>
            )
          },
          {
            path: 'batch-processes',
            element: (
              <FlaggedRoute flagKey="batch-processes-view">
                <GatedRoute capability="batch-processes:processes:read">
                  <BatchProcesses />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'batch-processes/:processId',
            element: (
              <FlaggedRoute flagKey="batch-processes-view">
                <GatedRoute capability="batch-processes:processes:read">
                  <BatchProcessDetailPage />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'automation',
            element: (
              <FlaggedRoute flagKey="automation-view">
                <GatedRoute capability="automation:rules:read">
                  <Automation />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          {
            path: 'automation/rules/:ruleId',
            element: (
              <FlaggedRoute flagKey="automation-view">
                <GatedRoute capability="automation:rules:read">
                  <AutomationRuleDetailPage />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          // The seller's request detail (TV1-2377 slice 7). `defaultRequestUrlFor`
          // has pointed here since the submit path shipped; without this entry the
          // success path handed her a 404. Flagged + gated exactly like its rule
          // sibling above — the Requests tab lives on the same `automation-view`
          // surface and the same `automation:rules:read` capability opens it.
          {
            path: 'automation/requests/:requestId',
            element: (
              <FlaggedRoute flagKey="automation-view">
                <GatedRoute capability="automation:rules:read">
                  <AutomationRequestDetailPage />
                </GatedRoute>
              </FlaggedRoute>
            )
          },
          // Operations review surface (TV1-1828). The propose → review journey
          // navigates here imperatively from the bulk-action entry points
          // (`useProposeOperation` → `/operations/review/:runId`). There is no
          // `operations` feature flag yet, so these routes are gated by the
          // `operations:runs:read` capability only — wrapping in a non-existent
          // flag would silently redirect to `/` and break the journey.
          {
            path: 'operations',
            element: (
              <GatedRoute capability="operations:runs:read">
                <OperationsPage />
              </GatedRoute>
            )
          },
          {
            path: 'operations/review/:runId',
            element: (
              <GatedRoute capability="operations:runs:read">
                <OperationReviewPage />
              </GatedRoute>
            )
          },
          {
            path: 'decision-matrix-demo',
            element: (
              <FlaggedRoute flagKey="dashboard">
                <DecisionMatrixDemo />
              </FlaggedRoute>
            )
          },
          {
            path: 'mcda-demo',
            element: (
              <FlaggedRoute flagKey="dashboard">
                <MCDADemo />
              </FlaggedRoute>
            )
          },
          // TV1-1690 — dev-only spreadsheet benchmark harness. Registered ONLY
          // when `isDevToolsEnabled`. The conditional spread ensures the route
          // literals do NOT appear in production builds (Vite drops the
          // branch + tree-shakes the dynamic imports above).
          ...( isDevToolsEnabled ? [
            {
              path: 'dev/spreadsheet-bench',
              element: ( <DevSpreadsheetBench /> )
            },
            {
              path: 'dev/spreadsheet-bench/scroll-spike',
              element: ( <DevSpreadsheetBenchScrollSpike /> )
            }
          ] : [])
        ]
      }
    ]
  },
  {
    path: 'not-found',
    element: <NotFoundTitle />
  },
  {
    path: '*',
    element: <Navigate to="/not-found" replace={true} />
  }
]);

export default router;

// Export component map for potential future use
export { COMPONENT_MAP };
